import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import StatusBadge from '../../components/common/StatusBadge';
import RichTextEditor from '../../components/common/RichTextEditor';
import ImageWithFallback from '../../components/common/ImageWithFallback';
import { useAuth } from '../../context/AuthContext';
import {
  getBannerList, createBanner, updateBanner, deleteBanner, uploadBannerImage, getBannerImageUrl,
} from '../../api/banner';
import { formatDateTime } from '../../utils/format';

/**
 * 메인페이지(front-web) 상단 배너 관리 화면.
 * 배너는 이미지 + 이동 링크 + 에디터로 입력한 설명(HTML) + 노출순서/노출여부로 구성된다.
 * 이미지는 상품 대표이미지와 동일한 패턴으로, 배너 정보를 먼저 저장한 뒤 반환된
 * bannerId 로 이어서 업로드한다 (신규 등록 시에도 동일하게 처리).
 */
// 헤더 검색창 좌측 돋보기 아이콘
function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
    </svg>
  );
}

export default function BannerManagePage() {
  const { auth } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [keyword, setKeyword] = useState('');

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getBannerList();
      setList(res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || '배너 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleCreate = async (form, imageFile) => {
    try {
      const res = await createBanner({ ...form, createdBy: auth.adminName });
      const bannerId = res.data?.bannerId;
      if (bannerId && imageFile) {
        await uploadBannerImage(bannerId, imageFile, auth.adminName);
      }
      setCreating(false);
      fetchList();
    } catch (err) {
      window.alert(err.response?.data?.message || '배너 등록에 실패했습니다.');
    }
  };

  const handleUpdate = async (form, imageFile) => {
    try {
      await updateBanner(editing.bannerId, { ...form, updatedBy: auth.adminName });
      if (imageFile) {
        await uploadBannerImage(editing.bannerId, imageFile, auth.adminName);
      }
      setEditing(null);
      fetchList();
    } catch (err) {
      window.alert(err.response?.data?.message || '배너 수정에 실패했습니다.');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteBanner(deleteTarget.bannerId);
      setDeleteTarget(null);
      fetchList();
    } catch (err) {
      window.alert(err.response?.data?.message || '배너 삭제에 실패했습니다.');
    }
  };

  // 헤더 검색: 배너명 부분일치(대소문자 무시)
  const filtered = list.filter((b) => !keyword || (b.title || '').toLowerCase().includes(keyword.trim().toLowerCase()));

  return (
    <AdminLayout title="배너관리" menuUrl="/admin/banner">
      {/* 타이틀 영역 */}
      <div className="page-header">
        <div>
          <h1>배너관리</h1>
          <div className="subtitle">메인페이지 상단 배너를 등록, 수정, 삭제합니다. 노출순서가 작을수록 먼저 노출됩니다.</div>
        </div>
      </div>

      {/* 헤더 영역: 검색 + 건수 / 등록 액션 */}
      <div className="list-toolbar">
        <div className="list-toolbar-left">
          <div className="list-search">
            <SearchIcon />
            <input className="input" placeholder="배너명 검색" value={keyword} onChange={(e) => setKeyword(e.target.value)} />
          </div>
        </div>
        <div className="list-toolbar-right">
          <button className="btn btn-primary" onClick={() => setCreating(true)}>+ 배너 등록</button>
        </div>
      </div>

      {/* 리스트 영역 */}
      <div className="list-count-bar">
        <span className="list-count">전체 <b>{filtered.length}</b>건</span>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>이미지</th><th>배너명</th><th>링크</th><th>이동방식</th><th>순서</th><th>노출</th><th>등록일</th><th>관리</th></tr></thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="empty-row">불러오는 중...</td></tr>}
              {!loading && error && <tr><td colSpan={8} className="empty-row error-state">{error}</td></tr>}
              {!loading && !error && list.length === 0 && <tr><td colSpan={8} className="empty-row">등록된 배너가 없습니다.</td></tr>}
              {!loading && !error && list.length > 0 && filtered.length === 0 && <tr><td colSpan={8} className="empty-row">검색 결과가 없습니다.</td></tr>}
              {!loading && !error && filtered.map((b) => (
                <tr key={b.bannerId}>
                  <td>
                    <div style={{ width: 72, height: 40, borderRadius: 4, overflow: 'hidden', background: 'var(--color-surface-alt)' }}>
                      {b.imagePath && (
                        <ImageWithFallback src={getBannerImageUrl(b.bannerId)} alt={b.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      )}
                    </div>
                  </td>
                  <td>{b.title}</td>
                  <td className="text-secondary" style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.linkUrl || '-'}</td>
                  <td>{b.linkUrl ? (b.linkTarget === '_blank' ? '새창' : '셀프') : '-'}</td>
                  <td>{b.sortOrder}</td>
                  <td><StatusBadge value={b.useYn} label={b.useYn === 'Y' ? '노출' : '숨김'} /></td>
                  <td>{formatDateTime(b.createdAt)}</td>
                  <td className="actions">
                    <button className="btn btn-secondary btn-sm" onClick={() => setEditing(b)}>수정</button>
                    <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(b)}>삭제</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {creating && <BannerFormModal onClose={() => setCreating(false)} onSave={handleCreate} />}
      {editing && <BannerFormModal banner={editing} onClose={() => setEditing(null)} onSave={handleUpdate} />}
      {deleteTarget && (
        <ConfirmDialog title="배너 삭제" message={`'${deleteTarget.title}' 배너를 삭제하시겠습니까?`}
          confirmLabel="삭제" danger onCancel={() => setDeleteTarget(null)} onConfirm={handleDelete} />
      )}
    </AdminLayout>
  );
}

function BannerFormModal({ banner, onClose, onSave }) {
  const isNew = !banner;
  const [form, setForm] = useState({
    title: banner?.title || '',
    linkUrl: banner?.linkUrl || '',
    linkTarget: banner?.linkTarget || '_self',
    content: banner?.content || '',
    sortOrder: banner?.sortOrder ?? 0,
    useYn: banner?.useYn || 'Y',
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [imageError, setImageError] = useState('');

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setImageError('');
  };

  const submit = async (e) => {
    e.preventDefault();
    if (isNew && !imageFile) {
      setImageError('배너 이미지를 선택하세요.');
      return;
    }
    setImageError('');
    setSaving(true);
    try {
      await onSave({ ...form, sortOrder: Number(form.sortOrder) }, imageFile);
    } finally {
      setSaving(false);
    }
  };

  const existingImageUrl = !isNew && banner.imagePath ? getBannerImageUrl(banner.bannerId) : null;

  return (
    <Modal title={isNew ? '배너 등록' : '배너 수정'} onClose={onClose} width={640}
      footer={<>
        <button className="btn btn-secondary" onClick={onClose}>취소</button>
        <button className="btn btn-primary" onClick={submit} disabled={saving}>{saving ? '저장 중...' : '저장'}</button>
      </>}>
      <form onSubmit={submit}>
        <div className="field">
          <label>배너명</label>
          <input className="input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="관리자 식별용 이름 (이미지 대체텍스트로도 사용)" />
        </div>

        <div className="field">
          <label>배너 이미지</label>
          <div
            onClick={() => document.getElementById('banner-image-input')?.click()}
            style={{
              width: '100%', maxWidth: 420, height: 140, borderRadius: 8, border: '1.5px dashed var(--color-border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
              overflow: 'hidden', background: 'var(--color-surface-alt)',
            }}
          >
            {imagePreview || existingImageUrl ? (
              <ImageWithFallback src={imagePreview || existingImageUrl} alt="미리보기" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <span className="text-secondary" style={{ fontSize: 12 }}>클릭하여 이미지 선택</span>
            )}
          </div>
          <input id="banner-image-input" type="file" accept="image/*" hidden onChange={handleImageSelect} />
          {imageError && <div className="text-danger" style={{ fontSize: 12.5, marginTop: 6 }}>{imageError}</div>}
        </div>

        <div className="field">
          <label>이동 링크 (선택)</label>
          <input className="input" value={form.linkUrl} onChange={(e) => setForm({ ...form, linkUrl: e.target.value })} placeholder="https:// 또는 /product/1 과 같은 내부 경로" />
        </div>

        <div className="field">
          <label>링크 이동방식</label>
          <div className="field-row" style={{ gap: 16 }}>
            <label className="checkbox-row" style={{ margin: 0 }}>
              <input type="radio" name="bannerLinkTarget" value="_self" checked={form.linkTarget === '_self'} onChange={() => setForm({ ...form, linkTarget: '_self' })} />
              <span>셀프 (현재창)</span>
            </label>
            <label className="checkbox-row" style={{ margin: 0 }}>
              <input type="radio" name="bannerLinkTarget" value="_blank" checked={form.linkTarget === '_blank'} onChange={() => setForm({ ...form, linkTarget: '_blank' })} />
              <span>새창</span>
            </label>
          </div>
        </div>

        <div className="field">
          <label>배너 설명 (선택)</label>
          <RichTextEditor
            value={form.content}
            onChange={(html) => setForm({ ...form, content: html })}
            placeholder="배너에 함께 노출할 설명을 입력하세요"
          />
        </div>

        <div className="field-row">
          <div className="field" style={{ maxWidth: 140 }}>
            <label>노출순서</label>
            <input className="input" type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: e.target.value })} />
          </div>
          <div className="field checkbox-row">
            <input type="checkbox" id="bannerUseYn" checked={form.useYn === 'Y'} onChange={(e) => setForm({ ...form, useYn: e.target.checked ? 'Y' : 'N' })} />
            <label htmlFor="bannerUseYn" style={{ margin: 0 }}>노출</label>
          </div>
        </div>
      </form>
    </Modal>
  );
}
